import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { LedgersService } from 'src/app/share/services/ledgers/ledgers.service';
import { DefaultConfig } from 'src/app/share/configs/plotly.config';

@Component({
    selector: 'app-performance-sale-trends',
    templateUrl: './performance-sale-trends.component.html',
    styleUrls: ['./performance-sale-trends.component.scss'],
    standalone: false
})
export class PerformanceSaleTrendsComponent implements OnChanges {


  @Input() selectedRange: any;
  @Input() selectedTyre: any;
  @Input() branchList: any[] = [];
  @Input() isBranchVisible: boolean = false;


  loading: boolean = false;
  errorMessage: string = '';

  subfilterOptions: any[] = [];
  selectedSubfilter: string = '';

  selectedBranch: string = '';

  public graph: any = {
    data: [],
    layout: {},
    config: {}
  };

  trendsList: any[] = [];
  maxValue: number = 0;

  constructor(private readonly ledgersService: LedgersService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRange']?.currentValue) {
      if (this.selectedRange) {
        this.setdropdownOptions();
      }
    }
  };

  setdropdownOptions() {
    if (this.selectedRange?.period == 7) {
      this.subfilterOptions = [
        { label: '7days', value: 'day' }
      ];
      this.selectedSubfilter = 'day';
    } else {
      this.subfilterOptions = [
        { label: 'Weekly', value: 'week' },
        { label: 'Monthly', value: 'month' }
      ];
      if (this.selectedRange?.period == 30) {
        this.selectedSubfilter = 'week';
      } else {
        this.selectedSubfilter = 'month';
      }
    }
    this.getData();
  }

  getData() {
    let payload: any = {
      tyreSize: this.selectedTyre.tyreSize,
      tyreMake: this.selectedTyre.tyreMake,
      tyreModel: this.selectedTyre.tyreModel,
      created_datefrom: this.selectedRange.startDate,
      created_dateupto: this.selectedRange.endDate,
      period: this.selectedSubfilter
    }
    if (this.selectedBranch) {
      payload.branch = this.selectedBranch;
    }
    this.getSaleInsights(payload);
  }


  onSubfilterChange(event: any) {
    this.selectedSubfilter = event.target.value;
    this.getData();
  }

  onBranchChange(event: any) {
    this.selectedBranch = event.target.value;
    this.getData();
  }

  getSaleInsights(payload: any) {
    this.loading = true;
    this.errorMessage = '';
    this.ledgersService.getSaleTrends(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.data) {
          this.initGraph(res.data);
        } else {
          this.errorMessage = "No data available for the selected period.";
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.errorMessage = error?.error?.message || "Failed to fetch data";
      }
    });
  }

  initGraph(apiData: any) {
    const originalLabels = apiData.labels || [];
    const customerSalesCount = apiData.salesToCustomers || [];
    const dealerSalesCount = apiData.saleToDealers || [];
    const customerSalesValue = apiData.saleValueToCustomers || apiData.saleValueFromCustomers || [];
    const dealerSalesValue = apiData.saleValueToDealers || [];
    const purchases = apiData.purchaseValue || [];

    const labels = originalLabels.map((label: string, index: number) => {
      const volume = (customerSalesCount[index] || 0) + (dealerSalesCount[index] || 0);
      return label + '\u200b'.repeat(index) + `<br>(Volume Sold: ${volume})`;
    });

    this.graph.data = [
      {
        x: labels,
        y: customerSalesValue,
        customdata: customerSalesCount,
        name: 'Sale to customers',
        mode: 'lines+markers',
        line: { color: '#00305C', width: 2 },
        marker: { size: 6, color: '#00305C' },
        hovertemplate: 'Sale to customers: ₹%{y:,.0f} (%{customdata})<extra></extra>',
      },
      {
        x: labels,
        y: dealerSalesValue,
        customdata: dealerSalesCount,
        name: 'Sale to dealers',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#7C3AED', width: 2 },
        marker: { size: 6, color: '#7C3AED' },
        hovertemplate: 'Sale to dealers: ₹%{y:,.0f} (%{customdata})<extra></extra>',
      },
      {
        x: labels,
        y: purchases,
        name: 'Purchase value',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#059669', width: 2 },
        marker: { size: 6, color: '#059669' },
        hovertemplate: 'Purchase value: ₹%{y:,.0f}<extra></extra>',
      },
    ];

    // Added for mobile bar charts
    this.trendsList = originalLabels.map((lbl: any, idx: number) => {
      const volume = (customerSalesCount[idx] || 0) + (dealerSalesCount[idx] || 0);
      return { label: lbl, value: volume };
    });
    this.maxValue = Math.max(...this.trendsList.map(item => item.value), 0);
    if (this.maxValue === 0) this.maxValue = 1;

    this.graph.layout = {
      autosize: true,
      height: 280,
      xaxis: {
        type: 'category',
        showgrid: false,
        zeroline: false,
        tickfont: { color: '#1e293b', size: 10, family: 'Arial, sans-serif' },
        automargin: true,
        showticklabels: true,
        tickangle: labels.length > 10 ? -45 : 0,
        fixedrange: true,
      },
      yaxis: {
        showgrid: true,
        gridcolor: '#f1f5f9',
        zeroline: true,
        zerolinecolor: '#e2e8f0',
        tickfont: { color: '#64748b', size: 10 },
        showticklabels: false,
        rangemode: 'tozero',
        fixedrange: true,
      },
      margin: { l: 25, r: 25, t: 30, b: 80 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: '#ffffff',
        font: { family: 'Arial', size: 12 },
        bordercolor: '#e2e8f0',
        namelength: -1
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: 1.15,
        font: { size: 10, color: '#64748b' },
        bgcolor: 'rgba(0,0,0,0)',
      }
    };

    this.graph.config = {
      ...DefaultConfig,
    };
  }


}
